import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { BookOpen } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import MembershipGate from "@/components/MembershipGate";

import MealDaySelector from "@/components/meals/MealDaySelector";
import AssignedMealPlanCard from "@/components/meals/AssignedMealPlanCard";
import DailyMacroSummary from "@/components/meals/DailyMacroSummary";
import DailyMealList from "@/components/meals/DailyMealList";
import MealDetailModal from "@/components/meals/MealDetailModal";
import NutritionPlanLibraryModal from "@/components/meals/NutritionPlanLibraryModal";
import DayMealPlanDialog from "@/components/meals/DayMealPlanDialog";
import MealPlanDetailsDialog from "@/components/meals/MealPlanDetailsDialog";
import MealPlanForm from "@/components/meals/MealPlanForm";
import MealPlanTemplateForm from "@/components/meals/MealPlanTemplateForm";

const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DAY_LONG = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Meals() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Current calendar date
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const todayISO = useMemo(() => isoDate(today), [today]);
  const todayDayIdx = (today.getDay() + 6) % 7;
  const todayDayKey = DAY_KEYS[todayDayIdx];

  // Week & selected date state
  const [weekStart, setWeekStart] = useState(() => startOfWeek(today));
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedDayKey, setSelectedDayKey] = useState(todayDayKey);

  // Sync selected day key whenever selectedDate changes
  const selectedDateISO = useMemo(() => isoDate(selectedDate), [selectedDate]);
  const isSelectedDateToday = selectedDateISO === todayISO;

  // Dialog and Modal states
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [selectedMealIndex, setSelectedMealIndex] = useState(null);
  const [dayAssignOpen, setDayAssignOpen] = useState(false);
  const [planDetailsOpen, setPlanDetailsOpen] = useState(false);
  const [planToView, setPlanToView] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  // Week navigation
  const handlePrevWeek = () => {
    const newWeek = addDays(weekStart, -7);
    setWeekStart(newWeek);
    const dayIdx = DAY_KEYS.indexOf(selectedDayKey);
    setSelectedDate(addDays(newWeek, dayIdx >= 0 ? dayIdx : 0));
  };

  const handleNextWeek = () => {
    const newWeek = addDays(weekStart, 7);
    setWeekStart(newWeek);
    const dayIdx = DAY_KEYS.indexOf(selectedDayKey);
    setSelectedDate(addDays(newWeek, dayIdx >= 0 ? dayIdx : 0));
  };

  const handleSelectDay = (dayKey, date) => {
    setSelectedDayKey(dayKey);
    setSelectedDate(date);
  };

  // Queries: User weekly assigned meal plan
  const { data: weeklyMealPlan = null } = useQuery({
    queryKey: ["userWeeklyMealPlan", user?.id],
    queryFn: () =>
      base44.entities.UserWeeklyMealPlan.filter({ user_id: user?.id }).then(
        (res) => res[0] || null
      ),
    enabled: !!user?.id,
  });

  // Templates
  const { data: templates = [] } = useQuery({
    queryKey: ["mealPlanTemplates"],
    queryFn: () => base44.entities.MealPlanTemplate.list(),
  });

  const templatesById = useMemo(() => {
    const map = new Map();
    templates.forEach((t) => map.set(t.id, t));
    return map;
  }, [templates]);

  // User's own templates & coach assigned meal plans
  const { data: userCreatedPlans = [] } = useQuery({
    queryKey: ["userCreatedMealPlans", user?.id],
    queryFn: () =>
      base44.entities.MealPlanTemplate.filter({ created_by_id: user?.id }, "-created_date"),
    enabled: !!user?.id,
  });

  const { data: coachAssignedPlans = [] } = useQuery({
    queryKey: ["coachAssignedMealPlans", user?.id],
    queryFn: async () => {
      const docs = await base44.entities.MealPlan.filter({ assigned_to_user_id: user?.id });
      return docs.sort((a, b) =>
        (b.last_assigned_at || b.created_date || "").localeCompare(
          a.last_assigned_at || a.created_date || ""
        )
      );
    },
    enabled: !!user?.id,
  });

  const combinedLibraryPlans = useMemo(() => {
    return [
      ...userCreatedPlans,
      ...coachAssignedPlans.map((p) => ({ ...p, _assigned: true })),
    ];
  }, [userCreatedPlans, coachAssignedPlans]);

  // Meal completions for the selected date
  const { data: mealCompletions = [] } = useQuery({
    queryKey: ["meal-completions", selectedDateISO, user?.id],
    queryFn: () =>
      base44.entities.MealCompletion.filter({
        user_id: user?.id,
        completed_date: selectedDateISO,
      }),
    enabled: !!user?.id,
  });

  // Manual meal logs for the selected date
  const { data: manualLogs = [] } = useQuery({
    queryKey: ["meal-logs", selectedDateISO, user?.id],
    queryFn: () =>
      base44.entities.MealLog.filter(
        { user_id: user?.id, completed_date: selectedDateISO },
        "created_date"
      ),
    enabled: !!user?.id,
  });

  // Recent logs across days to show indicator dots on day selector
  const { data: allRecentLogs = [] } = useQuery({
    queryKey: ["allRecentMealLogs", user?.id],
    queryFn: () => base44.entities.MealLog.list("-created_date", 200),
    enabled: !!user?.id,
  });

  const loggedDatesSet = useMemo(() => {
    const s = new Set();
    allRecentLogs.forEach((l) => {
      const d = l.completed_date || l.logged_date || (l.created_date || "").slice(0, 10);
      if (d) s.add(d);
    });
    return s;
  }, [allRecentLogs]);

  // Current day's assigned plan
  const assignedTemplateId = weeklyMealPlan?.[selectedDayKey];
  const assignedPlan = assignedTemplateId
    ? templatesById.get(assignedTemplateId) || null
    : null;

  // Resolve 7 days for the selector
  const weekDays = useMemo(() => {
    return DAY_KEYS.map((dk, idx) => {
      const date = addDays(weekStart, idx);
      const dISO = isoDate(date);
      const hasPlan = Boolean(weeklyMealPlan?.[dk]);
      const hasLogs = loggedDatesSet.has(dISO);
      const isToday = dISO === todayISO;

      return {
        dayKey: dk,
        dayIdx: idx,
        date,
        dateISO: dISO,
        hasPlan,
        hasLogs,
        isToday,
      };
    });
  }, [weekStart, weeklyMealPlan, loggedDatesSet, todayISO]);

  // Meals for the assigned plan
  const planMeals = useMemo(() => {
    if (!assignedPlan) return [];
    return (assignedPlan.meals || []).map((m) => ({
      ...m,
      plan_id: assignedPlan.id,
      plan_name: assignedPlan.name,
    }));
  }, [assignedPlan]);

  // Set of completed meal identifiers
  const completedMealsSet = useMemo(() => {
    const s = new Set();
    mealCompletions.forEach((mc) => {
      s.add(`${mc.meal_plan_id || ""}-${mc.meal_name}`);
    });
    return s;
  }, [mealCompletions]);

  // Nutrition & Calorie Totals
  const targetCalories = assignedPlan?.daily_calories || 0;
  const targetProtein = assignedPlan?.daily_protein_g || 0;
  const targetCarbs = assignedPlan?.daily_carbs_g || 0;
  const targetFat = assignedPlan?.daily_fat_g || 0;

  // Consumed calculations
  const consumedCalories = useMemo(() => {
    const planConsumed = planMeals
      .filter((m) => completedMealsSet.has(`${m.plan_id || ""}-${m.meal_name}`))
      .reduce((sum, m) => {
        return (
          sum +
          (m.foods || []).reduce((s, f) => s + (Number(f.calories) || 0), 0)
        );
      }, 0);

    const manualConsumed = manualLogs
      .filter((l) => l.completed)
      .reduce((s, l) => s + (Number(l.calories) || 0), 0);

    return planConsumed + manualConsumed;
  }, [planMeals, completedMealsSet, manualLogs]);

  const consumedProtein = useMemo(() => {
    const planP = planMeals
      .filter((m) => completedMealsSet.has(`${m.plan_id || ""}-${m.meal_name}`))
      .reduce((sum, m) => {
        return (
          sum +
          (m.foods || []).reduce((s, f) => s + (Number(f.protein_g) || 0), 0)
        );
      }, 0);
    const manualP = manualLogs
      .filter((l) => l.completed)
      .reduce((s, l) => s + (Number(l.protein_g) || 0), 0);
    return Math.round(planP + manualP);
  }, [planMeals, completedMealsSet, manualLogs]);

  const consumedCarbs = useMemo(() => {
    const planC = planMeals
      .filter((m) => completedMealsSet.has(`${m.plan_id || ""}-${m.meal_name}`))
      .reduce((sum, m) => {
        return (
          sum +
          (m.foods || []).reduce((s, f) => s + (Number(f.carbs_g) || 0), 0)
        );
      }, 0);
    const manualC = manualLogs
      .filter((l) => l.completed)
      .reduce((s, l) => s + (Number(l.carbs_g) || 0), 0);
    return Math.round(planC + manualC);
  }, [planMeals, completedMealsSet, manualLogs]);

  const consumedFat = useMemo(() => {
    const planF = planMeals
      .filter((m) => completedMealsSet.has(`${m.plan_id || ""}-${m.meal_name}`))
      .reduce((sum, m) => {
        return (
          sum +
          (m.foods || []).reduce(
            (s, f) => s + (Number(f.fat_g || f.fats_g) || 0),
            0
          )
        );
      }, 0);
    const manualF = manualLogs
      .filter((l) => l.completed)
      .reduce((s, l) => s + (Number(l.fat_g || l.fats_g) || 0), 0);
    return Math.round(planF + manualF);
  }, [planMeals, completedMealsSet, manualLogs]);

  // Meal Completion mutations
  const completeMutation = useMutation({
    mutationFn: (data) => base44.entities.MealCompletion.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meal-completions"] });
    },
  });

  const uncompleteMutation = useMutation({
    mutationFn: (id) => base44.entities.MealCompletion.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meal-completions"] });
    },
  });

  const handleToggleMeal = (meal) => {
    const key = `${meal.plan_id || ""}-${meal.meal_name}`;
    if (completedMealsSet.has(key)) {
      const match = mealCompletions.find(
        (mc) =>
          mc.meal_plan_id === (meal.plan_id || "") &&
          mc.meal_name === meal.meal_name
      );
      if (match) {
        uncompleteMutation.mutate(match.id);
      }
    } else {
      completeMutation.mutate({
        user_id: user?.id,
        meal_plan_id: meal.plan_id || "",
        meal_name: meal.meal_name,
        completed_date: selectedDateISO,
      });
    }
  };

  // Plan creation & deletion mutations
  const createPlanMutation = useMutation({
    mutationFn: (data) => base44.entities.MealPlanTemplate.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userCreatedMealPlans"] });
      queryClient.invalidateQueries({ queryKey: ["mealPlanTemplates"] });
      setShowCreateForm(false);
    },
  });

  const deletePlanMutation = useMutation({
    mutationFn: (id) => base44.entities.MealPlanTemplate.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userCreatedMealPlans"] });
      queryClient.invalidateQueries({ queryKey: ["mealPlanTemplates"] });
    },
  });

  const activeMeal =
    selectedMealIndex !== null && planMeals[selectedMealIndex]
      ? planMeals[selectedMealIndex]
      : null;

  const isCurrentActiveMealCompleted = activeMeal
    ? completedMealsSet.has(`${activeMeal.plan_id || ""}-${activeMeal.meal_name}`)
    : false;

  const dayLabel = isSelectedDateToday
    ? "Today"
    : DAY_LONG[DAY_KEYS.indexOf(selectedDayKey)];

  return (
    <MembershipGate>
      <div className="space-y-4 pb-20">
        {/* New Meals Page Header */}
        <div className="flex items-center justify-between pt-1">
          <div>
            <span className="text-[10px] sm:text-[11px] font-bold tracking-widest text-sky-400 uppercase block">
              Your Nutrition
            </span>
            <h1 className="text-2xl sm:text-3xl font-display font-black text-white tracking-tight">
              Fuel Your Day.
            </h1>
          </div>

          <button
            type="button"
            onClick={() => setLibraryOpen(true)}
            data-testid="open-plans-library-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-sky-500/20 border border-white/10 text-white hover:text-sky-300 font-bold text-xs transition active:scale-95 cursor-pointer shadow-sm"
          >
            <BookOpen className="w-3.5 h-3.5 text-sky-400" />
            <span>Plans</span>
          </button>
        </div>

        <HealthSourceNote topic="tracking" />
        {/* Compact Horizontal Day Selector */}
        <MealDaySelector
          weekStart={weekStart}
          onPrevWeek={handlePrevWeek}
          onNextWeek={handleNextWeek}
          weekDays={weekDays}
          selectedDayKey={selectedDayKey}
          onSelectDay={handleSelectDay}
        />

        {/* Featured Assigned Meal Plan Card */}
        <AssignedMealPlanCard
          plan={assignedPlan}
          onSwapPlan={() => setDayAssignOpen(true)}
          onViewPlanDetails={() => {
            setPlanToView(assignedPlan);
            setPlanDetailsOpen(true);
          }}
          onOpenLibrary={() => setLibraryOpen(true)}
        />

        {/* Daily Calories & Macros Summary */}
        <DailyMacroSummary
          consumedCalories={consumedCalories}
          targetCalories={targetCalories}
          consumedProtein={consumedProtein}
          targetProtein={targetProtein}
          consumedCarbs={consumedCarbs}
          targetCarbs={targetCarbs}
          consumedFat={consumedFat}
          targetFat={targetFat}
        />

        {/* Today's Meals List */}
        <DailyMealList
          dayLabel={dayLabel}
          meals={planMeals}
          completedMealsSet={completedMealsSet}
          onSelectMeal={(_meal, index) => setSelectedMealIndex(index)}
          onToggleMealQuick={handleToggleMeal}
        />

        {/* Dedicated Meal Detail Modal */}
        {activeMeal && (
          <MealDetailModal
            open={selectedMealIndex !== null}
            onClose={() => setSelectedMealIndex(null)}
            meal={activeMeal}
            mealIndex={selectedMealIndex}
            totalMeals={planMeals.length}
            isCompleted={isCurrentActiveMealCompleted}
            onToggleLog={() => handleToggleMeal(activeMeal)}
            onPrevMeal={() => {
              if (selectedMealIndex > 0) {
                setSelectedMealIndex(selectedMealIndex - 1);
              }
            }}
            onNextMeal={() => {
              if (selectedMealIndex < planMeals.length - 1) {
                setSelectedMealIndex(selectedMealIndex + 1);
              }
            }}
            isLogging={completeMutation.isPending || uncompleteMutation.isPending}
          />
        )}

        {/* Nutrition Plan Library Modal */}
        <NutritionPlanLibraryModal
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          plans={combinedLibraryPlans}
          onCreatePlan={() => setShowCreateForm(true)}
          onSelectPlan={(plan) => {
            setPlanToView(plan);
            setPlanDetailsOpen(true);
          }}
          onEditPlan={(plan) => {
            setEditingPlan(plan);
            setShowEditForm(true);
          }}
          onDeletePlan={(planId) => deletePlanMutation.mutate(planId)}
          onOpenScheduleDialog={(plan) => {
            setPlanToView(plan);
            setDayAssignOpen(true);
          }}
        />

        {/* Day-picker / Plan Swap Dialog */}
        {dayAssignOpen && (
          <DayMealPlanDialog
            open={dayAssignOpen}
            onOpenChange={setDayAssignOpen}
            day={selectedDayKey}
            dayName={DAY_LONG[DAY_KEYS.indexOf(selectedDayKey)]}
            userId={user?.id}
          />
        )}

        {/* Plan Details Dialog */}
        {planDetailsOpen && (
          <MealPlanDetailsDialog
            open={planDetailsOpen}
            onOpenChange={setPlanDetailsOpen}
            plan={planToView}
          />
        )}

        {/* Create Plan Form */}
        {showCreateForm && (
          <MealPlanForm
            open={showCreateForm}
            onOpenChange={setShowCreateForm}
            onSubmit={(data) => createPlanMutation.mutate(data)}
          />
        )}

        {/* Edit Plan Form */}
        {showEditForm && (
          <MealPlanTemplateForm
            open={showEditForm}
            onOpenChange={setShowEditForm}
            initialData={editingPlan}
            onSuccess={() => {
              setShowEditForm(false);
              setEditingPlan(null);
              queryClient.invalidateQueries({ queryKey: ["userCreatedMealPlans"] });
              queryClient.invalidateQueries({ queryKey: ["mealPlanTemplates"] });
            }}
          />
        )}
      </div>
    </MembershipGate>
  );
}